<template>
	<div class="kpis_tab report_theme_container" data-theme="ventas">
		<TabHeader
			title="KPIs"
			description="Indicadores de tu estado de resultados"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			calendarMode="range"
			:dateRange="calendarRange"
			:year-range="yearRange"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange"
			@update-year-range="updateYearRange">
			<!-- Filtro central del tab (spec "Graficos KPIs Resultados"): cada
				grupo muestra sus 4 gráficos. Mismo formato que los sub-filters
				de Ventas (Cuentas/Categorías/Clientes). -->
			<template #center-sub-nav>
				<div class="sub_nav_filters">
					<button v-for="group in kpiGroups" :key="group.id"
						type="button"
						class="filter_btn"
						:class="{ 'is-active': activeSubFilter === group.id }"
						@click="updateActiveSubFilter(group.id)">
						{{ group.label }}
					</button>
				</div>
			</template>
		</TabHeader>

		<!-- Grilla 2×2: los 4 gráficos del grupo activo. Ventas y Personas
			están cableados; Márgenes/Terceros llegan por fases → placeholders. -->
		<div class="kpi_grid mt-2">
			<template v-if="activeGroupImplemented">
				<article v-for="card in activeCards" :key="card.id" class="kpi_card">
					<header class="kpi_card_header">
						<span class="kpi_card_title">{{ card.title }}</span>
						<!-- Toggle del card (Boton YoY/Recur/$ de la spec): alterna
							entre las dos variantes de series, sin refetch. -->
						<button v-if="card.toggleLabel"
							type="button"
							class="kpi_toggle"
							:class="{ 'is-active': toggles[card.id] }"
							:aria-pressed="toggles[card.id] ? 'true' : 'false'"
							@click="toggles[card.id] = !toggles[card.id]">
							{{ card.toggleLabel }}
						</button>
					</header>
					<div class="kpi_card_body">
						<p v-if="error" class="kpi_state kpi_state--error">{{ error }}</p>
						<p v-else-if="isLoading" class="kpi_state">Cargando…</p>
						<p v-else-if="!hasKpisData" class="kpi_state">
							Sin datos para el período seleccionado.
						</p>
						<KpiComposedChart v-else
							:monthHeaders="kpisData.monthHeaders"
							:series="card.series" />
					</div>
				</article>
			</template>
			<template v-else>
				<article v-for="n in 4" :key="n" class="kpi_card kpi_card--placeholder">
					<div class="kpi_state">Próximamente</div>
				</article>
			</template>
		</div>
	</div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import { mapState, mapActions } from "vuex";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import KpiComposedChart from "@/components/financialReports/charts/KpiComposedChart.vue";
import { FILTER_PERIOD_OPTIONS } from "@/config/reportStructure";
import { defaultYearRange } from "@/store/financialReports/utils/dateRange";

// Grupos del filtro central (spec de la hoja "Graficos KPIs Resultados").
// Sólo "ventas" tiene backend hoy; el resto muestra placeholders y no fetchea.
const KPI_GROUPS = [
	{ id: "ventas", label: "Ventas", implemented: true },
	{ id: "margenes", label: "Márgenes", implemented: true },
	{ id: "personas", label: "Personas", implemented: true },
	{ id: "terceros", label: "Terceros", implemented: true },
];

// Multi-línea desde pares [nombre, serie]; omite las series null (no aplican
// al super tipo de la empresa, ej. margen producto en Servicios).
function lineSeries(entries, unit) {
	return entries
		.filter(([, data]) => data != null)
		.map(([name, data]) => ({ name, type: "line", unit, data }));
}

// Series por equipo desde el dict del backend ({ equipo: [valores] }).
function teamSeries(porEquipo, { type, unit, stack = null }) {
	return Object.entries(porEquipo || {}).map(([name, data]) => ({
		name, type, unit, data, ...(stack ? { stack } : {}),
	}));
}

export default {
	name: "KpisTab",

	components: {
		TabHeader,
		KpiComposedChart,
	},

	data() {
		return {
			filterOptions: FILTER_PERIOD_OPTIONS,
			kpiGroups: KPI_GROUPS,
			activeSubFilter: "ventas",
			// Toggle activo de cada card (false = variante base). Declarados
			// upfront — sin Vue.set (regla de migración).
			toggles: {
				g1: false, g2: false, g3: false, g4: false,
				g5: false, g6: false, g7: false, g8: false,
			},
			// Filtro de tiempo PROPIO del tab — independiente del store global
			// `reportsFilters` (que comparten los demás tabs), así sus consultas
			// no interfieren entre sí.
			filterPeriod: "month",
			endMonth: moment().toDate(),
			selectedPeriodRange: "lastYear",
			customRange: null,
			// Rango de años (granularidad Anual). Local como el resto de los
			// filtros de este tab — no comparte el reportsFilters global.
			customYearRange: null,
		};
	},

	computed: {
		...mapState("kpisReport", ["kpisData", "isLoading", "error"]),

		getNow() {
			return moment().toDate();
		},

		// Rango activo del calendar del header. Si hay `customRange` lo usamos
		// tal cual; si no, derivamos el último año a partir de endMonth para que
		// el calendar abra pintando un rango razonable en lugar de "—".
		calendarRange() {
			if (this.customRange) return this.customRange;
			const end = moment(this.endMonth).startOf("month");
			return { from: end.clone().subtract(11, "months").toDate(), to: end.toDate() };
		},

		// Rango de años del picker de Anual: el custom elegido, o el default de
		// Anual (mismo `defaultYearRange` que usa el fetch) para que el trigger
		// abra pintando el span real y nunca difiera de los gráficos.
		yearRange() {
			return this.customYearRange || defaultYearRange(this.endMonth);
		},

		hasKpisData() {
			return Boolean(
				this.kpisData
				&& this.kpisData.group === this.activeSubFilter
				&& this.kpisData.monthHeaders.length > 0,
			);
		},

		// Cards del grupo activo (cada grupo implementado arma sus 4 configs).
		activeCards() {
			if (this.activeSubFilter === "personas") return this.personasCards;
			if (this.activeSubFilter === "terceros") return this.tercerosCards;
			if (this.activeSubFilter === "margenes") return this.margenesCards;
			return this.ventasCards;
		},

		// Configs de los 4 cards del grupo Ventas (Gráficos 1-4 de la spec).
		// Cada toggle alterna series ya presentes en la respuesta — instantáneo.
		ventasCards() {
			if (!this.hasKpisData) {
				// Sin data igual se renderizan los 4 cards (con su estado
				// vacío/cargando); las series no se leen en ese caso.
				return [
					{ id: "g1", title: "Ventas y crecimiento", toggleLabel: "YoY %" },
					{ id: "g2", title: "Clientes y ticket promedio", toggleLabel: "Recurrentes" },
					{ id: "g3", title: "Clientes nuevos y salientes", toggleLabel: "$" },
					{ id: "g4", title: "Flujo de clientes sobre el total", toggleLabel: "$" },
				];
			}
			const { ventasCrecimiento, clientesTicket, flujoClientes, flujoRatios } = this.kpisData;
			// El crecimiento período-a-período sigue la granularidad activa
			// (MoM en mensual, QoQ en trimestral). En YTD/Anual el bucket
			// previo ES el año anterior → el toggle YoY se vuelve redundante
			// y se oculta.
			const popLabel = this.filterPeriod === "quarter"
				? "Crecimiento QoQ %"
				: (this.filterPeriod === "month" ? "Crecimiento MoM %" : "Crecimiento YoY %");
			const yoyToggleApplies = ["month", "quarter"].includes(this.filterPeriod);
			return [
				{
					id: "g1",
					title: "Ventas y crecimiento",
					toggleLabel: yoyToggleApplies ? "YoY %" : "",
					series: [
						{ name: "Ventas", type: "bar", unit: "money", data: ventasCrecimiento.ventas },
						this.toggles.g1 && yoyToggleApplies
							? { name: "Crecimiento YoY %", type: "line", unit: "percent", data: ventasCrecimiento.yoyPct }
							: { name: popLabel, type: "line", unit: "percent", data: ventasCrecimiento.momPct },
					],
				},
				{
					id: "g2",
					title: "Clientes y ticket promedio",
					toggleLabel: "Recurrentes",
					series: this.toggles.g2
						? [
							{ name: "Clientes Recurrentes", type: "bar", unit: "count", data: clientesTicket.recurrentes },
							{ name: "Ticket Promedio Recurrente ($)", type: "line", unit: "money", data: clientesTicket.arrpa },
						]
						: [
							{ name: "Clientes Totales", type: "bar", unit: "count", data: clientesTicket.clientes },
							{ name: "Ticket Promedio ($)", type: "line", unit: "money", data: clientesTicket.arpa },
						],
				},
				{
					id: "g3",
					title: "Clientes nuevos y salientes",
					toggleLabel: "$",
					series: this.toggles.g3
						? [
							{ name: "$ Nuevos Clientes", type: "bar", unit: "money", data: flujoClientes.nuevosAmount },
							{ name: "$ Clientes Salientes", type: "bar", unit: "money", data: flujoClientes.salientesAmount, down: true },
						]
						: [
							{ name: "Nuevos Clientes", type: "bar", unit: "count", data: flujoClientes.nuevos },
							{ name: "Clientes Salientes", type: "bar", unit: "count", data: flujoClientes.salientes, down: true },
						],
				},
				{
					id: "g4",
					title: "Flujo de clientes sobre el total",
					toggleLabel: "$",
					series: this.toggles.g4
						? [
							{ name: "$ Nuevos / Ventas %", type: "line", unit: "percent", data: flujoRatios.nuevosAmountPct },
							{ name: "$ Salientes / Ventas %", type: "bar", unit: "percent", data: flujoRatios.salientesAmountPct, down: true },
						]
						: [
							{ name: "Nuevos / Total %", type: "line", unit: "percent", data: flujoRatios.nuevosPct },
							{ name: "Salientes / Total %", type: "bar", unit: "percent", data: flujoRatios.salientesPct, down: true },
						],
				},
			];
		},

		// Configs de los 4 cards del grupo Personas (Gráficos 5-8 de la spec).
		// El toggle "Equipos" desglosa por la categoría que hace de equipo
		// (backend: team_category) — apiladas para $ y personas, multi-línea
		// para promedio y ratio.
		personasCards() {
			if (!this.hasKpisData) {
				return [
					{ id: "g5", title: "Remuneración total", toggleLabel: "Equipos" },
					{ id: "g6", title: "Personas remuneradas", toggleLabel: "Equipos" },
					{ id: "g7", title: "Remuneración promedio", toggleLabel: "Equipos" },
					{ id: "g8", title: "Remuneraciones / Ventas", toggleLabel: "Equipos" },
				];
			}
			const {
				remuneracionTotal, personas, remuneracionPromedio, remuneracionesVentas,
			} = this.kpisData;
			// Sin categoría de equipos no hay desglose → el toggle se oculta.
			const hasTeams = Boolean(this.kpisData.teamCategory);
			const toggleLabel = hasTeams ? "Equipos" : "";
			return [
				{
					id: "g5",
					title: "Remuneración total",
					toggleLabel,
					series: this.toggles.g5 && hasTeams
						? teamSeries(remuneracionTotal.porEquipo, { type: "bar", unit: "money", stack: "equipos" })
						: [{ name: "Remuneración Total ($)", type: "bar", unit: "money", data: remuneracionTotal.total }],
				},
				{
					id: "g6",
					title: "Personas remuneradas",
					toggleLabel,
					series: this.toggles.g6 && hasTeams
						? teamSeries(personas.porEquipo, { type: "bar", unit: "count", stack: "equipos" })
						: [{ name: "Personas", type: "bar", unit: "count", data: personas.total }],
				},
				{
					id: "g7",
					title: "Remuneración promedio",
					toggleLabel,
					series: this.toggles.g7 && hasTeams
						? teamSeries(remuneracionPromedio.porEquipo, { type: "line", unit: "money" })
						: [{ name: "Remuneración Promedio ($)", type: "line", unit: "money", data: remuneracionPromedio.total }],
				},
				{
					id: "g8",
					title: "Remuneraciones / Ventas",
					toggleLabel,
					series: this.toggles.g8 && hasTeams
						? teamSeries(remuneracionesVentas.porEquipoPct, { type: "line", unit: "percent" })
						: [{ name: "Remuneraciones / Ventas %", type: "line", unit: "percent", data: remuneracionesVentas.totalPct }],
				},
			];
		},

		// Configs de los 4 cards del grupo Terceros (Gráficos 9-12): proveedores
		// (facturas afectas/exentas excl. código 46) y honorarios. Sin toggles.
		tercerosCards() {
			if (!this.hasKpisData) {
				return [
					{ id: "g9", title: "Compras a proveedores" },
					{ id: "g10", title: "Proveedores y ticket promedio" },
					{ id: "g11", title: "Honorarios" },
					{ id: "g12", title: "Personas a honorarios y promedio" },
				];
			}
			const { proveedores, honorarios } = this.kpisData;
			return [
				{
					id: "g9",
					title: "Compras a proveedores",
					series: [
						{ name: "Proveedores ($)", type: "bar", unit: "money", data: proveedores.amount },
						{ name: "Proveedores / Ventas %", type: "line", unit: "percent", data: proveedores.ventasPct },
					],
				},
				{
					id: "g10",
					title: "Proveedores y ticket promedio",
					series: [
						{ name: "N° de Proveedores", type: "bar", unit: "count", data: proveedores.count },
						{ name: "Proveedor Promedio ($)", type: "line", unit: "money", data: proveedores.promedio },
					],
				},
				{
					id: "g11",
					title: "Honorarios",
					series: [
						{ name: "Honorarios ($)", type: "bar", unit: "money", data: honorarios.amount },
						{ name: "Honorarios / Ventas %", type: "line", unit: "percent", data: honorarios.ventasPct },
					],
				},
				{
					id: "g12",
					title: "Personas a honorarios y promedio",
					series: [
						{ name: "N° Personas Honorarios", type: "bar", unit: "count", data: honorarios.count },
						{ name: "Honorario Promedio ($)", type: "line", unit: "money", data: honorarios.promedio },
					],
				},
			];
		},

		// Configs de los 4 cards del grupo Márgenes (Gráficos 13-15 de la spec).
		// Sin toggles; las series que no aplican al super tipo se omiten.
		margenesCards() {
			if (!this.hasKpisData) {
				return [
					{ id: "g13", title: "Promedios por cliente" },
					{ id: "g14", title: "Márgenes %" },
					{ id: "g13b", title: "Promedios por persona" },
					{ id: "g15", title: "Gastos / Ventas %" },
				];
			}
			const {
				promedioCliente, margenesPct, promedioPersona, gastosVentasPct,
			} = this.kpisData;
			return [
				{
					id: "g13",
					title: "Promedios por cliente",
					series: lineSeries([
						["Ticket Promedio (ARPA)", promedioCliente.arpa],
						["Margen Bruto Promedio (AMPA)", promedioCliente.ampa],
						["Margen Contribución Promedio (ACMPA)", promedioCliente.acmpa],
						["EBITDA Promedio (AEPA)", promedioCliente.aepa],
					], "money"),
				},
				{
					id: "g14",
					title: "Márgenes %",
					series: lineSeries([
						["Margen Producto", margenesPct.producto],
						["Margen Bruto", margenesPct.bruto],
						["Margen Contribución", margenesPct.contribucion],
						["Margen EBITDA", margenesPct.ebitda],
						["Margen Neto", margenesPct.neto],
					], "percent"),
				},
				{
					id: "g13b",
					title: "Promedios por persona",
					series: lineSeries([
						["Venta por Persona", promedioPersona.venta],
						["Margen Bruto por Persona", promedioPersona.margenBruto],
						["Margen Contribución por Persona", promedioPersona.margenContribucion],
						["EBITDA por Persona", promedioPersona.ebitda],
					], "money"),
				},
				{
					id: "g15",
					title: "Gastos / Ventas %",
					series: lineSeries([
						["Gastos Totales / Ventas", gastosVentasPct.total],
						["Gastos Admin / Ventas", gastosVentasPct.administracion],
						["Gastos Comerciales / Ventas", gastosVentasPct.comerciales],
						["Gastos I+D / Ventas", gastosVentasPct.iD],
					], "percent"),
				},
			];
		},

		// Firma reactiva: un solo trigger de fetch aunque cambien varios filtros
		// en la misma transacción.
		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.filterPeriod,
				this.activeSubFilter,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
				this.customYearRange && this.customYearRange.from,
				this.customYearRange && this.customYearRange.to,
			].join("|");
		},

		activeGroupImplemented() {
			const group = this.kpiGroups.find((g) => g.id === this.activeSubFilter);
			return Boolean(group && group.implemented);
		},
	},

	methods: {
		...mapActions("kpisReport", ["fetchKpisResultados"]),

		loadCharts() {
			// Los grupos sin backend todavía no fetchean (placeholders).
			if (!this.activeGroupImplemented) return;
			this.fetchKpisResultados({
				endMonth: this.endMonth,
				selectedPeriodRange: this.selectedPeriodRange,
				customRange: this.customRange,
				customYearRange: this.customYearRange,
				period: this.filterPeriod,
				group: this.activeSubFilter,
			});
		},
		updateActiveSubFilter(group) {
			this.activeSubFilter = group;
		},
		updateEndMonth(date) {
			this.endMonth = date;
		},
		updateDateRange(range) {
			if (!range || !range.from || !range.to) return;
			this.customRange = range;
			this.selectedPeriodRange = "custom";
			this.endMonth = range.to;
		},
		// Rango de años (Anual). Ortogonal al mes: no toca endMonth ni
		// selectedPeriodRange.
		updateYearRange(range) {
			if (!range || !range.from || !range.to) return;
			this.customYearRange = range;
		},
		updateFilterPeriod(period) {
			this.filterPeriod = period;
		},
	},

	mounted() {
		this.loadCharts();
	},

	watch: {
		fetchSignature() {
			this.loadCharts();
		},
	},
};
</script>

<style scoped lang="scss">
.kpis_tab {
	position: relative;
	height: 100%;
}

.kpi_grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-auto-rows: 340px;
	gap: 16px;
	width: 100%;

	@media (max-width: 1024px) {
		grid-template-columns: 1fr;
	}
}

.kpi_card {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 16px;
	box-shadow: var(--report-shadow-card);
	padding: 12px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.kpi_card_header {
	flex-shrink: 0;
	margin-bottom: 8px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
}

.kpi_card_title {
	color: var(--report-text);
	font-size: 14px;
	font-weight: 600;
}

// Toggle del card (Boton YoY/Recur/$ de la spec): pill chico que alterna la
// variante de series. Activo = primary del tab, mismo lenguaje que los
// sub-filters del header.
.kpi_toggle {
	flex-shrink: 0;
	padding: 3px 10px;
	border: 1px solid var(--report-border);
	border-radius: 999px;
	background: var(--report-bg);
	font-size: 12px;
	font-weight: 600;
	font-family: inherit;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.15s ease;

	&:hover {
		background: var(--report-surface);
	}

	&.is-active {
		background: var(--report-primary);
		border-color: var(--report-primary);
		color: var(--report-bg);
	}
}

.kpi_card_body {
	flex: 1;
	min-height: 0;
	position: relative;
	display: flex;
	align-items: stretch;
	justify-content: stretch;
}

.kpi_card--placeholder {
	align-items: center;
	justify-content: center;
	border-style: dashed;
}

.kpi_state {
	margin: auto;
	color: var(--report-text-muted);
	font-size: 13px;
	text-align: center;

	&--error {
		color: var(--report-negative);
	}
}

// Sub-filters del header inyectados por slot (los estilos scoped de TabHeader
// no alcanzan al markup del slot) — mismas classes que usa Ventas.
.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	font-family: inherit;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: var(--report-surface);
	}

	&.is-active {
		background: var(--report-primary);
		color: var(--report-bg);
	}
}
</style>
